// ── Configuration ────────────────────────────────────────────────────────
export const CX = 400, CY = 400;
export const UNIT = 33.3;
export const BASE_SIZE = 84;

// Ring layout — each ring holds up to SLOT_COUNT_PER_RING apps. Apps fill
// rings from the inside outward: the inner ring fills to 12 before any
// spill over into the middle ring, and so on. A 20-app configuration
// therefore looks like 12 inner + 8 middle + empty outer; 25 apps would
// be 12 + 12 + 1. This is a core OS guideline — do not change the
// slot→ring arithmetic without preserving it.
export const RINGS = [
  { count: 12, radius: 5 * UNIT, startAngle: 0, endAngle: 360, angleOffset: 0,  rotOffset: 0,  scale: 0.8 },
  { count: 12, radius: 7 * UNIT, startAngle: 0, endAngle: 360, angleOffset: 15, rotOffset: 0,  scale: 1.0 },
  { count: 12, radius: 9 * UNIT, startAngle: 0, endAngle: 360, angleOffset: 0,  rotOffset: 0,  scale: 1.2 },
];
export const SLOT_COUNT_PER_RING = 12;
export const EXTRA_RING_RADIUS_STEP = RINGS.length > 1
  ? Math.abs(RINGS[RINGS.length - 1]!.radius - RINGS[RINGS.length - 2]!.radius)
  : UNIT;
export const EXTRA_RING_SCALE_CAP = RINGS[RINGS.length - 1]!.scale;
export const EXTRA_RING_OFFSET_A = RINGS.length > 1
  ? RINGS[RINGS.length - 2]!.angleOffset
  : RINGS[RINGS.length - 1]!.angleOffset;
export const EXTRA_RING_OFFSET_B = RINGS[RINGS.length - 1]!.angleOffset;

// Push-away effect (Unity values)
export const PUSH_MAX   = 1.25 * UNIT;
export const PUSH_RANGE = 13.5 * UNIT;
export const PUSH_LERP  = 0.12;
export const PUSH_BASE_CLEARANCE = 12;
export const PUSH_MID_RING_CLEARANCE_BONUS = 6;
export const PUSH_OUTER_RING_CLEARANCE_BONUS = 16;
export const PUSH_MID_RING_RANGE_MULT = 1.14;
export const PUSH_OUTER_RING_RANGE_MULT = 1.28;
export const PUSH_MID_RING_FORCE_MULT = 1.12;
export const PUSH_OUTER_RING_FORCE_MULT = 1.34;
export const PUSH_MAX_CAP = 2.7 * UNIT;
export const PUSH_INTER_RING_CLEARANCE = 18;
export const PUSH_COLLISION_PASSES = 3;
export const PUSH_OUTER_ESCAPE_BLEND = 0.72;
export const PUSH_OUTER_HIGHLIGHT_INNER_RANGE_MULT = 1.12;
export const PUSH_OUTER_HIGHLIGHT_INNER_FORCE_MULT = 1.22;
export const PUSH_OUTER_HIGHLIGHT_INNER_CLEARANCE_BONUS = 10;
export const PUSH_INNER_MIDDLE_CLEARANCE = 16;
export const PUSH_INNER_COLLISION_PASSES = 3;
export const PUSH_INNER_CENTER_BIAS = 0.78;
export const PUSH_MIDDLE_OUTWARD_NUDGE = 0.28;
export const FLUID_TARGET_LERP_BASE = 0.1;
export const FLUID_TARGET_LERP_BOOST = 0.2;
export const FLUID_POS_LERP_ACTIVE = 0.18;
export const FLUID_POS_LERP_IDLE = 0.11;
export const FLUID_SCALE_LERP_PRESSED = 0.22;
export const FLUID_SCALE_LERP_HIGHLIGHT = 0.17;
export const FLUID_SCALE_LERP_RELEASE = 0.0325;

// Organism layout engine
export const ORGANISM_PAIR_GAP = 12;
export const ORGANISM_PAIR_PASSES = 4;
export const ORGANISM_ANCHOR_PULL = 0.22;
export const ORGANISM_SCALE_SOLVE_LERP = 0.32;
export const ORGANISM_OUTPUT_LERP_MULT = 0.95;
export const ORGANISM_SCALE_MIN = 0.52;
export const ORGANISM_SCALE_MAX = 2.1;
export const ORGANISM_BOUNDARY_PADDING = 4;
export const ORGANISM_TUBE_INNER_EDGE_PAD = 8;
export const ORGANISM_TUBE_OUTER_EDGE_PAD = 12;
export const ORGANISM_PROPAGATION_RANGE = 240;
export const ORGANISM_PROPAGATION_STRENGTH = 0.32;
export const ORGANISM_PROPAGATION_MAX_SHRINK = 0.4;
export const ORGANISM_DENSITY_SHRINK = 0.16;
export const ORGANISM_COLLISION_SHRINK = 0.06;
export const ORGANISM_OBSTACLE_GAP = 10;
export const ORGANISM_OBSTACLE_DYNAMIC_GAP_MIN = 8;
export const ORGANISM_OBSTACLE_DYNAMIC_GAP_MAX = 16;
export const ORGANISM_OBSTACLE_DYNAMIC_GAP_SMOOTHING = 0.28;
export const ORGANISM_NEIGHBOR_CAP_PASSES = 3;
export const ORGANISM_NEIGHBOR_CAP_MARGIN = 8;
export const ORGANISM_NEIGHBOR_CAP_PULL = 0.85;
export const ORGANISM_FINAL_PASSES = 3;
export const ORGANISM_FINAL_PUSH = 0.7;
export const ORGANISM_FINAL_SHRINK = 0.24;
export const ORGANISM_FINAL_MAX_SHRINK = 0.22;
export const ORGANISM_FINAL_PRESSURE_DECAY = 0.4;
export const ORGANISM_PRIMARY_SOLVER_PRIORITY = 4.5;
export const ORGANISM_PRIMARY_FINAL_PIN_MOVE = 0;
export const ORGANISM_PRIMARY_REPEL_PASSES = 2;
export const ORGANISM_PRIMARY_REPEL_SHRINK = 0.08;
export const ORGANISM_PRIMARY_REPEL_RANGE = 260;
export const ORGANISM_OBSTACLE_ARC_INFLUENCE = 120;
export const ORGANISM_OBSTACLE_ARC_EDGE_GAP = 12;
export const ORGANISM_OBSTACLE_ARC_SMOOTHING = 0.24;
export const ORGANISM_OBSTACLE_ARC_RADIUS_LERP = 0.2;
export const ORGANISM_OBSTACLE_ARC_MIN_ITEMS = 4;
export const ORGANISM_OBSTACLE_ARC_RADIUS_FIT_BLEND = 0.18;
export const ORGANISM_OBSTACLE_ARC_RADIUS_MAX_EXTRA = 16;

// Global scaling
export const HIGHLIGHT_SIZE = 100;  // absolute px size when highlighted (same for all rings)
export const SCALE_LERP     = 0.15;

// Camera — matching grid mode behavior
export const CAMERA_SMOOTHING = 0.25;   // same as gridCameraSmoothing
export const CAMERA_MAX_PAN_BASE = 405;
export const CAMERA_MAX_PAN_PADDING = 24;
export const CAMERA_MAX_PAN_CAP = 760;
export const CAMERA_MAX_PAN_LERP = 0.18;
export const GYRO_MAX_TILT_DEG = 15;

export const UNLOCK_WINDOW_MS = 1000;
export const REQUIRED_PRESSES = 1;
export const UNLOCK_DURATION_MS = 1000;
export const FEEDBACK_BASE_SCALE = 1.0;
export const FEEDBACK_PRESS_SCALES = [0.65]; // single press shrink
export const FEEDBACK_SPRING_STIFF = 180;
export const FEEDBACK_SPRING_DAMP = 0.82;
export const PERSISTENT_CLOCK_ENABLED = true;
// Render every app icon + its connector line in a single solid color.
// true  = all icons & lines use SOLID_ICON_COLOR (ignores per-app edgeColor
//         and the random COLORS palette — covers still render on top).
// false = default random-palette behaviour.
export const USE_SOLID_ICON_COLOR = true;
export const SOLID_ICON_COLOR = '#e2e2e2';
export const MINI_CLOCK_SCALE = 0.5;
export const LOCKING_DURATION_MS = 1000;
export const LOCK_WORLD_ZOOM = 2.6;
export const MENU_SCALE_FINAL = 1.35;
export const LOCK_ICON_REVEAL_START = 0.3;
export const LOCK_LINE_ALPHA = 1.0;
export const LOCK_LINE_STUB_FRACTION = 0.14;
export const LOCK_LINE_CENTER_PULL_MAX = 34; // max px the center tip pulls inward on lock press
export const LOCK_LINE_START_HIDE_FACTOR = 0.55; // bury line start slightly under icon edge
export const HOME_LINE_START_HIDE_FACTOR = 0.4; // must match home line renderer to avoid handoff jump
export const LOCK_LINE_COLLIDER_WIDTH = 10;
export const LOCK_HOUR_LINE_COUNT = 12;
export const LOCK_HOUR_LINE_NUB_INSET = 9;    // px inward from viewport edge for inner tip
export const LOCK_HOUR_LINE_WIDTH = 3.375;     // 2.25 * 1.5
// 1-indexed cap on which ring rows render connector lines. Rings are
// counted outward from ringIndex 0. Caps both home-state lines and
// lock-state nubs identically, so colliders follow. Effective ceiling
// is RINGS.length; values above are harmless (no items beyond it).
// Default 1 = only the innermost ring draws.
export const MAX_LINE_ROWS = 1;
export const INSTALLER_TEST_MANIFEST_URL = 'https://onljswkegixyjjhpcldn.supabase.co/functions/v1/get-manifest?id=76068854-f447-4927-adb1-75f062ba51dd&format=json';
export const INSTALLER_CENTER_EPSILON = 2;
export const INSTALLER_SCANNER_SIZE_RATIO = 0.5;
// Minimum gap between the scanner circle edge and the clock guard zone
// (LINE_CENTER_RADIUS). Mirrors the hover-grow clearance behaviour.
export const INSTALLER_SCANNER_CLOCK_CLEARANCE = 8;
// Safety margin between the scanner circle and the scene edge — scene is
// overflow:hidden at 800×800, so exceeding this clips the scanner.
export const INSTALLER_SCANNER_SCENE_EDGE_PAD = 10;
export const INSTALLER_SCAN_TEXT_MS = 4500;
export const INSTALLER_SCANNER_TRANSITION_MS = 430;
export const INSTALLER_SCANNER_FADE_START = 0.6;
export const INSTALLER_SCANNER_OBSTACLE_PADDING = 0;
export const INSTALLER_CENTERING_TIMEOUT_MS = 950;
export const INSTALLER_BIRTH_OFFSET_PAD = 14;
export const INSTALLER_BIRTH_DURATION_MS = 460;
export const INSTALLER_BIRTH_RELEASE_MS = 320;
export const INSTALLER_TRAVEL_MS = 1180;
export const INSTALLER_CROWD_REPEL_RANGE = 168;
export const INSTALLER_CROWD_REPEL_MAX = 56;
export const INSTALLER_CROWD_CLEARANCE = 14;
export const INSTALLER_CROWD_SMOOTHING = 0.18;
export const INSTALLER_CROWD_SMOOTHING_RETURN = 0.1;
export const INSTALLER_SCALE_GROW_FRACTION = 0.78;
export const INSTALLER_SCALE_RIPPLE_AMP = 0.12;
export const INSTALLER_SCALE_RIPPLE_WAVES = 4.5;
export const INSTALLER_SCALE_RIPPLE_DAMP = 3.2;
export const INSTALLER_BIRTH_HAPTIC_STRENGTH = 0.1;
export const INSTALLER_BIRTH_HAPTIC_DELAY_MS = 55;
export const INSTALLER_INSTALL_MS = 1750;
export const APP_PRESS_DOWN_SCALE = 0.6;
export const APP_PRESS_DOWN_MS = 86;
export const APP_PRESS_REBOUND_MS = 240;
export const SELECTOR_HYSTERESIS_PX = 16;
export const SELECTOR_RING_SWITCH_BIAS_PX = 9;
export const SELECTOR_MIDDLE_RING_BONUS = 0.08;
export const SELECTOR_MAX_DIST_MIN_PX = 96;
export const SELECTOR_MAX_DIST_PADDING_PX = 28;
export const SELECTOR_RELEASE_BONUS_PX = 18;
// ── Options menu geometry ────────────────────────────────────────────────
// Scene-coordinate layout for the two-row options list. Kept in sync with
// the matching CSS in index.html (see `.home-options-list`, `.home-option`,
// `#home-option-edit`). These constants are the single source of truth for
// TS-side geometry — the connector path is precomputed from them so the
// rAF loop never needs to read DOM layout.
export const OPTIONS_LIST_LEFT_PX = 610;
// The list is now vertically centered around this Y (matches CSS
// `top: <this>px` + `transform: translateY(-50%)` on `.home-options-list`),
// so regardless of how many options are visible the stack straddles CY.
export const OPTIONS_LIST_CENTER_Y_PX = 400;
export const OPTIONS_OPTION_GAP_PX = 12;
export const OPTIONS_OPTION_LINE_HEIGHT_SELECTED_PX = 20;
export const OPTIONS_OPTION_LINE_HEIGHT_UNSELECTED_PX = 17;

export const HOME_OPTIONS_HOLD_MS = 420;
export const HOME_OPTIONS_ENTER_MS = 6000;
export const HOME_OPTIONS_ZOOM_MS = 500;
export const HOME_OPTIONS_EXIT_MS = 1000;
export const HOME_OPTIONS_ZOOM_OUT_MS = 500;
// Phase of HOME_OPTIONS_EXIT_MS at which the icon-shrink + line-trim
// portion begins. Starts roughly halfway through the camera zoom-out so
// the user sees the shrink WHILE returning to home, not after a stable
// pause. 0.17 ≈ 250ms after commit when EXIT_MS=1500, which is half of
// HOME_OPTIONS_ZOOM_OUT_MS=500.
export const HOME_OPTIONS_REMOVE_DELAY_FRAC = 0.17;
export const HOME_OPTIONS_WORLD_ZOOM = 1.9;
export const HOME_OPTIONS_CAMERA_SMOOTHING = 0.32;
export const HOME_OPTIONS_HIDE_STAGGER_SPAN = 0.68;
export const HOME_OPTIONS_HIDE_FADE_SPAN = 0.32;
export const HOME_OPTIONS_TWIST_THRESHOLD_DEG = 13;
export const HOME_OPTIONS_TWIST_DEADZONE_DEG = 4;
export const HOME_OPTIONS_TWIST_COOLDOWN_MS = 160;
export const HOME_EDIT_DRAG_WORLD_LERP = 0.36;
// Per-frame lerp factor for the dragged icon's scale as it crosses rings.
// Smaller = smoother/slower transition. ~0.12 converges to a new ring's
// natural scale over ~400ms — visible smooth resize, no pop.
export const HOME_EDIT_DRAG_SCALE_LERP = 0.12;
// Black-tint strength applied to other apps + their lines while the user
// is in edit-home-screen mode, so the focused / dragged app stands out.
// 0.4 = 40% darker — heavier than the installer dim, since edit mode
// benefits from a stronger contrast between focus and rest.
export const HOME_EDIT_DIM_STRENGTH = 0.4;
// Per-frame lerp factor for the edit-mode dim. Slower than the installer
// scanner's 0.22 so the fade in/out on entering and leaving edit mode is
// perceptibly smooth (~370ms to reach 90%) rather than the near-instant
// 80ms of the scanner's reactive dim. Also softens the dim/bright
// transfer when the cursor moves between icons in edit mode.
export const HOME_EDIT_DIM_LERP = 0.1;
export const INSTALLER_SCANNER_DIM_STRENGTH_DEFAULT = 0.2;
export const INSTALLER_SCANNER_DIM_LERP = 0.22;
export const INSTALLER_STATES = {
  IDLE: 'idle',
  CENTERING: 'centering',
  SCANNER_OPENING: 'scanner_opening',
  SCANNING: 'scanning',
  PROCESSING: 'processing',
  SCANNER_CLOSING: 'scanner_closing',
  INSERTING: 'inserting',
  INSTALLING: 'installing',
} as const;
